import { useEffect, useState } from "react";
import { getTranslateByKey, isNullUndefinedEmpty, removeEmptyValues } from "@/main/services/utils";
import KogModalSideComponent from "@/main/components/modal/ModalSide";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import { toast } from "react-toastify";
import * as S from "./styles";
import { damageSubCategoryApi } from "@/main/api";
import { useForm } from "react-hook-form";
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";
import { DamageSubCategoryRequest } from "@/main/models/request/damageSubCategoryRequest";
import { KeyValue } from "@/main/models/common";
import useAsyncModalConfirmReason from "@/main/components/shared/asyncModalConfirmReason/asyncModalConfirmReason";
import { ReasonTypeEnum } from "@/main/services/enum/ReasonEnum";
import { toBase64 } from "@/main/services/utilsFile";
import { DamageCategoryResponse, DamageSubCategoryResponse } from "@/main/models/response/damageCategoryResponse";

interface Props {
	open?: boolean;
	closeModal: () => void;
	damageCategory: DamageCategoryResponse | null;
	damageSubCategoryEdit?: DamageSubCategoryResponse | null;
	loadGridList: () => void;
}

function DamageSubCategorySidebar({ open, closeModal, damageCategory, damageSubCategoryEdit, loadGridList }: Props) {
	const [isLoading, setIsLoading] = useState(false);
	const [getConfirmationReason, ModalConfirmationReason] = useAsyncModalConfirmReason();

	interface IDamageSubCategoryForm {
		damageSubCategoryName: string;
		minimumParts: number | null;
		maximumParts: number | null;
		repairDays: number | null;
	}
	const INITIAL_FORM_STATE: IDamageSubCategoryForm = {
		damageSubCategoryName: "",
		minimumParts: null,
		maximumParts: null,
		repairDays: null,
	};

	const {
		setValue,
		handleSubmit,
		control,
		reset,
		getValues,
		formState: { errors },
	} = useForm<IDamageSubCategoryForm>({
		mode: "onChange",
		defaultValues: INITIAL_FORM_STATE,
	});

	useEffect(() => {
		if (damageSubCategoryEdit) {
			setValue("damageSubCategoryName", damageSubCategoryEdit?.damageSubCategoryName);
			setValue("maximumParts", damageSubCategoryEdit?.maxParts);
			setValue("minimumParts", damageSubCategoryEdit?.minParts);
			setValue("repairDays", damageSubCategoryEdit?.repairDays);
		} else {
			reset(INITIAL_FORM_STATE);
		}
	}, [open]);

	function validateForm() {
		let isValid = true;
		const data = getValues();
		if (isNullUndefinedEmpty(data.damageSubCategoryName)) {
			isValid = false;
		}

		if (isNullUndefinedEmpty(data.minimumParts)) {
			isValid = false;
		}

		if (isNullUndefinedEmpty(data.maximumParts)) {
			isValid = false;
		}

		if (isNullUndefinedEmpty(data.repairDays)) {
			isValid = false;
		}

		if (
			!isNullUndefinedEmpty(data.minimumParts) &&
			!isNullUndefinedEmpty(data.maximumParts) &&
			Number(data.minimumParts) > Number(data.maximumParts)
		) {
			toast.error(getTranslateByKey("information.MinimumPartsGreaterThanMaximum", true));

			isValid = false;
		}

		return isValid;
	}

	function getModalTitle() {
		if (damageSubCategoryEdit) {
			return `${getTranslateByKey("information.editSubCategory")} - ${damageCategory?.damageCategoryName}`;
		} else {
			return `${getTranslateByKey("information.addSubCategory")} - ${damageCategory?.damageCategoryName} `;
		}
	}

	async function onSubmitSave(data: IDamageSubCategoryForm) {
		if (!validateForm()) return;

		const resultConfirm = await getConfirmationReason(
			getTranslateByKey("information.confirmChangeTitle"),
			ReasonTypeEnum.ChangeCompany,
		);

		if (!resultConfirm.confirmed) return;

		let filesRequest = undefined;
		if (resultConfirm.files && resultConfirm.files.length > 0) {
			filesRequest = await Promise.all(
				resultConfirm.files.map(async (item) => {
					const base64DataUrl = await toBase64(item);
					return {
						fileName: item.name,
						contentType: item.type,
						fileBase64: base64DataUrl as string,
					};
				}),
			);
		}

		setIsLoading(true);

		const requestDamageSubCategory = removeEmptyValues({
			guidDamageCategory: damageCategory?.guidDamageCategory,
			damageSubCategoryName: data.damageSubCategoryName,
			minimumParts: data.minimumParts,
			maximumParts: data.maximumParts,
			repairDays: data.repairDays,
			guidDamageSubCategory: damageSubCategoryEdit?.guidDamageSubCategory,
			changeJustification: {
				guidReason: resultConfirm?.reason ? String(resultConfirm.reason.key) : null,
				reasonDescription: resultConfirm?.observation || null,
				filesUpload: filesRequest,
			},
		}) as DamageSubCategoryRequest;

		damageSubCategoryApi
			.saveDamageSubCategory(requestDamageSubCategory)
			.then(() => {
				toast.success(getTranslateByKey("information.subCategorySaveSuccessfully"));

				reset(INITIAL_FORM_STATE);
				loadGridList();
				closeModal();
			})
			.catch((error: any) => {
				error.errorMessages.forEach((e: KeyValue) => {
					toast.error(e.value);
				});
			})
			.finally(() => setIsLoading(false));
	}

	return (
		<>
			{open && (
				<KogModalSideComponent open={open} closeModal={() => closeModal()} id="id-damageSubCategory-modal-scroll">
					<form onSubmit={handleSubmit(onSubmitSave)} noValidate>
						<S.HeaderText>{getModalTitle()}</S.HeaderText>
						<S.Container>
							<KogInputTextControlledNew
								id="id-subcategory-name"
								name="damageSubCategoryName"
								label={getTranslateByKey("information.name")}
								control={control}
								controllerProps={{ rules: { required: true } }}
								errorMessageCustom={
									errors.damageSubCategoryName?.type === "required"
										? getTranslateByKey("information.subCategoryNameError", true)
										: ""
								}
							/>
							<KogInputTextControlledNew
								id={`id-subcategory-minimumParts`}
								name={"minimumParts"}
								label={getTranslateByKey("information.minimumParts")}
								control={control}
								hasOnlyNumberInteger={true}
								inputProps={{
									maxLength: 5,
								}}
								controllerProps={{ rules: { required: true } }}
								errorMessageCustom={
									errors.minimumParts?.type === "required"
										? getTranslateByKey("information.MinimumPartsNotInformed", true)
										: ""
								}
							/>
							<KogInputTextControlledNew
								id={`id-subcategory-maximumParts`}
								name={"maximumParts"}
								label={getTranslateByKey("information.maximumParts")}
								control={control}
								hasOnlyNumberInteger={true}
								inputProps={{
									maxLength: 5,
								}}
								controllerProps={{ rules: { required: true } }}
								errorMessageCustom={
									errors.maximumParts?.type === "required"
										? getTranslateByKey("information.MaximumPartsNotInformed", true)
										: ""
								}
							/>
							<KogInputTextControlledNew
								id={`id-subcategory-repairDays`}
								name={"repairDays"}
								label={getTranslateByKey("information.repairDays")}
								control={control}
								hasOnlyNumberInteger={true}
								inputProps={{
									maxLength: 5,
								}}
								controllerProps={{ rules: { required: true } }}
								errorMessageCustom={
									errors.maximumParts?.type === "required"
										? getTranslateByKey("information.RepairDaysNotInformed", true)
										: ""
								}
							/>
						</S.Container>
						<S.AreaButton>
							<SecondaryButton id="id-damageSubCategory-btn-save" type="submit" isLoading={isLoading}>
								{getTranslateByKey("information.save")}
							</SecondaryButton>
						</S.AreaButton>
					</form>
				</KogModalSideComponent>
			)}
			<ModalConfirmationReason closeByOverlay={false} />
		</>
	);
}

export default DamageSubCategorySidebar;
